import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

/**
 * Tiny contextual help link. Drop into a page header:
 *
 *   <HelpHint section="contacts-list" />
 *
 * It renders a circled `?` that navigates to `/help#<section>` — the Help
 * Center auto-expands and scrolls to that entry. Whenever a new page ships,
 * add a `<HelpHint>` to it and a matching entry in `helpContent.ts` (see
 * `.claude/rules/help-sync.md`).
 */
export function HelpHint({
  section,
  className,
}: {
  /** Entry id (slug) from `helpContent.ts` — used as the URL hash. */
  section: string;
  className?: string;
}) {
  const t = useT();
  return (
    <Link
      to={`/help#${section}`}
      aria-label={t('help.hintAria')}
      title={t('help.hintAria')}
      className={cn(
        'inline-grid h-7 w-7 place-items-center rounded-full border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] text-[var(--color-fg-muted)] transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]',
        className,
      )}
    >
      <HelpCircle className="h-4 w-4" />
    </Link>
  );
}
